<template lang="pug">
  .b-customer-chats-page
    ._head
      router-link._back(:to="specRoute") ‹ К спецификации
      h1._title Чаты
    ._error(v-if="loadError") {{ loadError }}
    SpecChatScreen(
      v-else
      :chats="chats"
      :activeChat="activeChat"
      :messages="messages"
      :canCreate="false"
      :canWrite="canWrite"
      @select="openChat"
      @send="send"
      @back="closeChat"
      )
</template>

<script>
import SpecChatScreen from '@/components/spec-chat/SpecChatScreen.vue';
import model from '@/model/specCustomer';
import { apiRun } from '@/model/apiRun';

/**
 * Чаты заказчика по персональной ссылке (ТЗ 6.10, маршрут `/ext/spec/{идентификатор}/{подпись}/chats`):
 * чат с дизайнером и трёхсторонние чаты со своим участием. Экран — тот же общий компонент ленты, что
 * у дизайнера и поставщика; различия роли переданы свойствами (ТЗ 12.6): создавать чаты заказчик не
 * может, позиции и запрос в системном событии у него не кликабельны (ТЗ 6.10).
 *
 * В архивном проекте поле ввода скрыто по признаку «только чтение» из ответа сервера (ТЗ 3.5, 6.10).
 * Сгенерировано Claude.
 */
export default {
  name: 'CustomerSpecChatsPage',
  components: { SpecChatScreen },
  data() {
    return {
      chats: [],
      activeChat: null,
      messages: [],
      loadError: '',
    };
  },
  computed: {
    link() { return model.linkOf(this.$route); },
    specRoute() { return { name: 'ext-spec-customer', params: { ...this.link } }; },
    routeChatId() { return Number(this.$route.params.chatId) || null; },
    canWrite() { return !this.activeChat?.readOnly; },
  },
  watch: {
    routeChatId(id) {
      if (id && this.activeChat?.id !== id) this.selectById(id);
    },
  },
  created() {
    this.load();
  },
  methods: {
    /** Сообщение об отказе выбирает модель по коду ответа — как на странице таблицы (ТЗ 6.9). */
    async load() {
      const { items, error } = await model.fetchChats(this.link);
      this.loadError = error ?? '';
      if (error) return;
      this.chats = items;
      if (this.routeChatId) this.selectById(this.routeChatId);
    },
    selectById(id) {
      const chat = this.chats.find((c) => c.id === id);
      if (chat) this.openChat(chat, false);
    },
    async openChat(chat, navigate = true) {
      this.activeChat = chat;
      this.messages = await model.fetchMessages(this.link, chat.id);
      if (navigate && this.routeChatId !== chat.id) {
        this.$router.replace({
          name: 'ext-spec-customer-chat',
          params: { ...this.link, chatId: String(chat.id) },
        });
      }
    },
    /** Возврат к списку на узких экранах: список и лента показываются поочерёдно (ТЗ 6.10). */
    closeChat() {
      this.activeChat = null;
      this.messages = [];
      if (this.routeChatId) {
        this.$router.replace({ name: 'ext-spec-customer-chats', params: { ...this.link } });
      }
    },
    async send({ text, files }) {
      if (!this.activeChat) return;
      const sent = await apiRun(
        () => model.sendMessage(this.link, this.activeChat.id, text, files),
        'Не удалось отправить сообщение',
      );
      if (!sent) return;
      this.messages = await model.fetchMessages(this.link, this.activeChat.id, false);
      const { items } = await model.fetchChats(this.link, false);
      this.chats = items ?? this.chats;
    },
  },
};
</script>

<style scoped lang="scss">
.customer-chats-page {
  box-sizing: border-box;
  min-height: 100vh;
  padding: 24px 24px 60px;
  background: #F5F7FB;

  &__head {
    display: flex;
    align-items: baseline;
    gap: 16px;
    margin-bottom: 16px;
  }

  &__back {
    color: var(--color-accent);
    font-size: 13px;
  }

  &__title {
    font-size: 28px;
    font-weight: 700;
    margin: 0;
  }

  &__error {
    max-width: 640px;
    margin: 60px auto;
    padding: 24px;
    border-radius: 8px;
    background: var(--color-white);
    text-align: center;
  }

  @media (max-width: 768px) {
    padding: 16px 12px 40px;

    &__title {
      font-size: 22px;
    }
  }
}
</style>
